import {
  type ProductResource,
  ProductType,
  type VariantComponentResource,
  type VariantResource,
} from "@exonn/js-sdk";
import {
  type ProductRowResource,
  ProductRowType,
} from "src/framework/listV2/types.ts";

export const mapProductResourceToProductRow = (
  resource: ProductResource | VariantResource | VariantComponentResource,
): ProductRowResource => {
  if ("type" in resource) {
    // product
    return {
      id: resource.id,
      type: {
        [ProductType.MULTIPLE]: ProductRowType.Multiple,
        [ProductType.COMPLEX]: ProductRowType.Complex,
        [ProductType.SINGLE]: ProductRowType.Single,
        [ProductType.SET]: ProductRowType.Set,
      }[resource.type],
      title: resource.title || resource.variants?.[0]?.title || "",
      short_description: resource.short_description,
      status: resource.status,
      sku: resource.sku,
      images: resource.images,
      weight: resource.weight,
      unit_quantity: resource.unit_quantity,
      price: resource.price,
      descendants_count: getProductDescendantsCount(resource),

      path: [resource.id],
    };
  }
  if ("is_product_set" in resource) {
    return {
      id: resource.id,
      type: getVariantType(resource),
      title: resource.title || resource.product!.title || "",
      short_description:
        resource.short_description || resource.product!.short_description || "",
      status: resource.status,
      sku: resource.sku,
      images: resource.images,
      weight: resource.weight || resource.product!.weight || "",
      price: resource.price || "",
      unit_quantity:
        resource.unit_quantity || resource.product!.unit_quantity || "",
      descendants_count: resource.components?.length || 0,

      path: getVariantPath(resource),
    };
  }
  if ("component_variant" in resource) {
    return {
      id: resource.id,
      type: getVariantComponentType(resource),
      title:
        resource?.component_variant?.title ||
        resource.component_variant?.product?.title ||
        "",
      short_description:
        resource?.component_variant?.short_description ||
        resource.component_variant?.product?.short_description ||
        "",
      status: resource.variant!.status,
      sku: resource?.component_variant?.sku || "",
      images: resource.variant!.images,
      weight:
        resource?.component_variant?.weight ||
        resource.component_variant?.product?.weight ||
        "",
      unit_quantity:
        resource?.component_variant?.unit_quantity ||
        resource.component_variant?.product?.unit_quantity ||
        "",
      price:
        resource?.component_variant?.price ||
        resource.component_variant?.product?.price ||
        "",
      descendants_count: 0,

      path: getVariantComponentPath(resource),
    };
  }
  throw new Error("unknown type");
};

const getProductDescendantsCount = (resource: ProductResource) => {
  if (resource.type === ProductType.SINGLE) {
    return 0;
  }
  if (
    resource.type === ProductType.COMPLEX ||
    resource.type === ProductType.MULTIPLE
  ) {
    return resource.variants!.length;
  }
  if (resource.type === ProductType.SET) {
    return resource.variants![0]!.components!.length;
  }
  return 0;
};

const getVariantType = (variant: VariantResource) => {
  if (variant.product!.type === ProductType.COMPLEX) {
    return variant.is_product_set
      ? ProductRowType.ComplexSetVariant
      : ProductRowType.ComplexVariant;
  }
  return ProductRowType.MultipleVariant;
};

const getVariantComponentType = (component: VariantComponentResource) => {
  if (component.variant!.product!.type === ProductType.COMPLEX) {
    return ProductRowType.ComplexSetVariantComponent;
  }
  return ProductRowType.SetComponent;
};

const getVariantComponentPath = (component: VariantComponentResource) => {
  if (component.variant!.product!.type === ProductType.COMPLEX) {
    return [
      component.variant!.product!.id,
      component.variant!.id,
      component.id,
    ];
  }
  return [component.variant!.product!.id, component.id];
};

const getVariantPath = (variant: VariantResource) => {
  return [variant.product!.id, variant.id];
};
